import { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import {
  X,
  FileText,
  History,
  Database,
  ExternalLink,
  Bookmark,
  BookmarkCheck,
  Share2,
  Calendar,
  Link2,
  AlertCircle,
  Check,
} from 'lucide-react';
import type { LegacyQuoteCard } from '@/types';
import { Button } from '@/components/ui/button';

interface QuoteDetailModalProps {
  card: LegacyQuoteCard | null;
  isOpen: boolean;
  onClose: () => void;
  isSaved: boolean;
  onAddToTrail: () => void;
  onShare: () => void;
}

const laneIcons = {
  A: FileText,
  B: History,
  C: Database,
};

const laneLabels = {
  A: 'Primary Text',
  B: 'Contemporaneous Record',
  C: 'Modern Official Data',
};

const laneColors = {
  A: 'bg-blue-500',
  B: 'bg-amber-500',
  C: 'bg-green-500',
};

export function QuoteDetailModal({
  card,
  isOpen,
  onClose,
  isSaved,
  onAddToTrail,
  onShare,
}: QuoteDetailModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen && card) {
      gsap.fromTo(
        overlayRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.2, ease: 'power2.out' }
      );
      gsap.fromTo(
        modalRef.current,
        { scale: 0.95, opacity: 0, y: 20 },
        { scale: 1, opacity: 1, y: 0, duration: 0.3, ease: 'power2.out' }
      );
    }
  }, [isOpen, card]);

  const handleClose = () => {
    gsap.to(modalRef.current, {
      scale: 0.95,
      opacity: 0,
      y: 20,
      duration: 0.2,
      ease: 'power2.in',
    });
    gsap.to(overlayRef.current, {
      opacity: 0,
      duration: 0.2,
      delay: 0.1,
      ease: 'power2.in',
      onComplete: onClose,
    });
  };

  if (!isOpen || !card) return null;

  const Icon = laneIcons[card.lane];

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[300] flex items-center justify-center bg-[#0B0D10]/60 p-4 backdrop-blur-sm"
      onClick={(e) => e.target === overlayRef.current && handleClose()}
    >
      <div
        ref={modalRef}
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-[rgba(11,13,16,0.08)] p-6">
          <div className="flex items-center gap-3">
            <div
              className={`h-10 w-10 ${laneColors[card.lane]} flex items-center justify-center rounded-xl`}
            >
              <Icon className="h-5 w-5 text-white" />
            </div>
            <div>
              <p className="font-medium text-[#0B0D10]">{laneLabels[card.lane]}</p>
              <p className="font-mono text-xs text-[#6B6F78]">Lane {card.lane}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="rounded-lg p-2 transition-colors hover:bg-[#F6F5F2]"
            aria-label="Close receipt details"
          >
            <X className="h-5 w-5 text-[#6B6F78]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Source Badge */}
          <div className="mb-4 flex items-center gap-2">
            <span className="rounded-full bg-[#F6F5F2] px-3 py-1 text-sm text-[#6B6F78]">
              {card.source}
            </span>
            <span className="font-mono text-sm text-[#E1062C]">{card.date}</span>
          </div>

          {/* Document Title */}
          <h2 className="font-display mb-6 text-xl font-bold text-[#0B0D10]">
            {card.documentTitle}
          </h2>

          {/* Quote */}
          <div className="mb-6 rounded-xl border-l-4 border-[#E1062C] bg-[#F6F5F2] p-6">
            <p className="text-lg font-medium leading-relaxed text-[#0B0D10]">{card.quote}</p>
          </div>

          {/* Full Context */}
          {card.context && (
            <div className="mb-6">
              <h3 className="mb-3 font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                Surrounding Context
              </h3>
              <p className="text-sm leading-relaxed text-[#6B6F78]">{card.context}</p>
            </div>
          )}

          {/* Metadata */}
          <div className="mb-6 grid grid-cols-2 gap-4">
            {card.effectiveDate && (
              <div className="rounded-xl bg-[#F6F5F2] p-4">
                <div className="mb-1 flex items-center gap-2">
                  <Calendar className="h-4 w-4 text-[#E1062C]" />
                  <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Effective Date
                  </span>
                </div>
                <p className="font-medium text-[#0B0D10]">{card.effectiveDate}</p>
              </div>
            )}
            {card.amendedBy && (
              <div className="rounded-xl bg-[#F6F5F2] p-4">
                <div className="mb-1 flex items-center gap-2">
                  <Link2 className="h-4 w-4 text-[#E1062C]" />
                  <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Amended By
                  </span>
                </div>
                <p className="font-medium text-[#0B0D10]">{card.amendedBy}</p>
              </div>
            )}
            {card.supersededBy && (
              <div className="rounded-xl bg-[#F6F5F2] p-4">
                <div className="mb-1 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-500" />
                  <span className="font-mono text-xs uppercase tracking-[0.08em] text-[#6B6F78]">
                    Superseded By
                  </span>
                </div>
                <p className="font-medium text-[#0B0D10]">{card.supersededBy}</p>
              </div>
            )}
          </div>

          {/* Source indicator */}
          <div className="flex items-center gap-4 rounded-xl bg-green-50 p-4">
            <Check className="h-5 w-5 text-green-600" />
            <div>
              <p className="text-sm font-medium text-green-800">Canonical source linked</p>
              <p className="text-xs text-green-600">Open the source to verify this receipt.</p>
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="border-t border-[rgba(11,13,16,0.08)] bg-[#F6F5F2] p-6">
          <div className="flex items-center gap-3">
            <Button
              onClick={onAddToTrail}
              variant={isSaved ? 'default' : 'outline'}
              className={`flex flex-1 items-center justify-center gap-2 ${
                isSaved
                  ? 'bg-[#E1062C] text-white hover:bg-[#c00525]'
                  : 'border-[#0B0D10] text-[#0B0D10] hover:bg-[#0B0D10] hover:text-white'
              }`}
            >
              {isSaved ? <BookmarkCheck className="h-4 w-4" /> : <Bookmark className="h-4 w-4" />}
              {isSaved ? 'Saved to Trail' : 'Add to Trail'}
            </Button>
            <Button
              onClick={onShare}
              className="flex flex-1 items-center justify-center gap-2 bg-[#0B0D10] text-white hover:bg-[#1a1d23]"
            >
              <Share2 className="h-4 w-4" />
              Share Card
            </Button>
            <a
              href={card.canonicalLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1"
            >
              <Button
                variant="outline"
                className="flex w-full items-center justify-center gap-2 border-[rgba(11,13,16,0.2)] text-[#0B0D10] hover:bg-[#0B0D10] hover:text-white"
              >
                <ExternalLink className="h-4 w-4" />
                Open Source
              </Button>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
